/**
 * OffBlockBadge — renders a dispatch category block (PTO / Contract Job /
 * Site Visit …) on any calendar surface. Colour comes from the category
 * palette via the `--cat` CSS variable so `.cat-block` (index.css) can
 * pick light or dark treatment without any JS theme detection.
 *
 * iter269 — the dispatcher's instructions for the day (materials, site
 * contacts, "meet Mark at the loading dock") live in `block.notes` and
 * used to be hard-truncated with no way to read the rest. Tapping the
 * block now expands it in place on every surface, tech included.
 */
import { useState } from "react";
import { categoryColor } from "@/lib/dispatchCategories";

export function OffBlockBadge({ block, categories, size = "md", testId }) {
  const [open, setOpen] = useState(false);
  if (!block) return null;
  const color = categoryColor(categories, block.reason);
  const hours = block.hours ? `${block.hours}h` : "";
  const notes = (block.notes || "").trim();
  const detail = [hours, notes].filter(Boolean).join(" · ");
  const small = size === "sm";
  const expandable = notes.length > 0;
  // One row per block: category left, note in the middle, hours pinned right.
  return (
    <div
      className={`cat-block rounded border border-black/5 flex-1 flex flex-wrap items-center ${small ? "px-1.5 py-0.5 min-h-[18px] gap-x-1.5" : "px-2 py-1.5 gap-x-2"} ${expandable ? "cursor-pointer" : ""}`}
      style={{ "--cat": color }}
      data-testid={testId}
      data-expanded={open ? "true" : "false"}
      title={expandable ? `${block.reason || "Off"} — ${detail} · tap to ${open ? "collapse" : "read the full note"}` : `${block.reason || "Off"}${detail ? ` — ${detail}` : ""}`}
      onClick={expandable ? (e) => { e.stopPropagation(); setOpen(o => !o); } : undefined}
    >
      <span className={`font-bold uppercase tracking-wide truncate ${small ? "text-[8px]" : "text-[11px]"}`}>{block.reason || "Off"}</span>
      {expandable && !open && (
        <span className={`${small ? "text-[7px]" : "text-[9px]"} opacity-80 truncate flex-1 min-w-0`}>{notes}</span>
      )}
      {expandable && (
        <span className={`${small ? "text-[7px]" : "text-[9px]"} font-semibold opacity-70 shrink-0`} data-testid={testId ? `${testId}-toggle` : undefined}>
          {open ? "▲" : "▼"}
        </span>
      )}
      {hours && (
        <span className={`${small ? "text-[8px]" : "text-[10px]"} font-semibold opacity-80 ml-auto shrink-0`} data-testid={testId ? `${testId}-hours` : undefined}>{hours}</span>
      )}
      {open && (
        <div className={`w-full ${small ? "text-[8px] leading-snug" : "text-[10px] leading-snug"} opacity-90 whitespace-pre-wrap break-words mt-0.5`}
             data-testid={testId ? `${testId}-note` : undefined}>
          {notes}
        </div>
      )}
    </div>
  );
}

export default OffBlockBadge;
